import { useQuery } from "@tanstack/react-query"
import { App, Button, Card, Input, Skeleton, Space, Table, Tag, Typography } from "antd"
import type { ColumnsType } from "antd/es/table"
import { useState } from "react"
import { useTheme } from "styled-components"
import { ContainerFull } from "../components/GeneralComponents"
import { ShowMessagePage } from "../config"
import { generateUnsubscribeUrl, getUnsubscribedEmails } from "../endpoints"

const { Search } = Input
const { Title, Text } = Typography

interface UnsubscribeRecord {
    email: string
    unsubscribedAt: string
    ipAddress: string
    userAgent: string
    resubscribedAt: string | null
}

// Component for generating unsubscribe URLs
const UnsubscribeUrlGenerator = (): JSX.Element => {
    const { message: antMessage } = App.useApp()
    const theme = useTheme()
    const [email, setEmail] = useState<string>("")
    const [baseUrl, setBaseUrl] = useState<string>(
        process.env.NODE_ENV === 'development' 
            ? "http://localhost:8181" 
            : "https://www.plabsequencing.com"
    )
    const [generatedUrl, setGeneratedUrl] = useState<string>("")
    const [isLoading, setIsLoading] = useState<boolean>(false)

    const handleGenerate = async () => {
        if (!email) {
            antMessage.warning("Please enter an email address")
            return
        }

        // Validate email format
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
        if (!emailRegex.test(email)) {
            antMessage.error("Please enter a valid email address")
            return
        }

        setIsLoading(true)
        try {
            const response = await generateUnsubscribeUrl({ email, baseUrl })
            if (response.code === 1000 && response.data) {
                setGeneratedUrl(response.data.unsubscribeUrl)
                antMessage.success("Unsubscribe URL generated successfully!")
            } else {
                antMessage.error(response.message || "Failed to generate unsubscribe URL")
            }
        } catch (error) {
            console.error("Error generating unsubscribe URL:", error)
            antMessage.error("Failed to generate unsubscribe URL")
        } finally {
            setIsLoading(false)
        }
    }

    const copyToClipboard = async () => {
        try {
            await navigator.clipboard.writeText(generatedUrl)
            antMessage.success("URL copied to clipboard!")
        } catch (error) {
            console.error("Failed to copy to clipboard:", error)
            antMessage.error("Failed to copy to clipboard")
        }
    }

    return (
        <Space direction="vertical" size="middle" style={{ width: "100%" }}>
            <Typography.Text type="secondary">
                Generate secure unsubscribe URLs for marketing emails. The token expires in 30 days.
            </Typography.Text>
            
            <Space size="middle" wrap>
                <Input
                    placeholder="Enter email address"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    style={{ width: "300px" }}
                    size="middle"
                />
                <Input
                    placeholder="Base URL"
                    value={baseUrl}
                    onChange={(e) => setBaseUrl(e.target.value)}
                    style={{ width: "250px" }}
                    size="middle"
                />
                <Button
                    type="primary"
                    onClick={handleGenerate}
                    loading={isLoading}
                    disabled={!email}
                    size="middle"
                    style={{ backgroundColor: theme.primary }}
                >
                    Generate URL
                </Button>
            </Space>

            {generatedUrl && (
                <Space direction="vertical" size="small" style={{ width: "100%" }}>
                    <Typography.Text strong>Generated Unsubscribe URL:</Typography.Text>
                    <Space size="middle" style={{ width: "100%" }}>
                        <Input.TextArea
                            value={generatedUrl}
                            readOnly
                            rows={3}
                            style={{ flex: 1, fontFamily: "monospace", fontSize: "12px" }}
                        />
                        <Button onClick={copyToClipboard} size="middle">
                            Copy
                        </Button>
                    </Space>
                    <Typography.Text type="secondary" style={{ fontSize: "12px" }}>
                        This URL can be used in marketing emails. It includes a secure token that expires in 30 days.
                    </Typography.Text>
                </Space>
            )}
        </Space>
    )
}

export const Unsubscribes = (): JSX.Element => {
    const [page, setPage] = useState<number>(1)
    const [pageSize, setPageSize] = useState<number>(50)
    const [search, setSearch] = useState<string>("")

    const { isPending, isError, data, error } = useQuery({
        queryKey: ["getUnsubscribedEmails", page, pageSize, search],
        queryFn: () => getUnsubscribedEmails({ page, pageSize, search }),
    })

    const handleSearch = (value: string) => {
        setSearch(value)
        setPage(1) // Reset to first page when searching
    }

    const handleTableChange = (pagination: any) => {
        setPage(pagination.current)
        setPageSize(pagination.pageSize)
    }

    if (isPending) {
        return (
            <ContainerFull style={{ fontFamily: '"Rubik", sans-serif' }}>
                <Skeleton active />
            </ContainerFull>
        )
    }

    if (isError) {
        return ShowMessagePage("Error", error?.message ?? "Unknown error occurred")
    }

    if (data?.code !== 1000) {
        return ShowMessagePage("Error", data?.message ?? "Failed to load unsubscribed emails")
    }

    const columns: ColumnsType<UnsubscribeRecord> = [
        {
            title: "Email",
            dataIndex: "email",
            key: "email",
            width: "25%",
            render: (email: string) => (
                <Text copyable style={{ fontFamily: "monospace" }}>
                    {email}
                </Text>
            ),
            sorter: (a, b) => a.email.localeCompare(b.email),
        },
        {
            title: "Status",
            key: "status",
            width: "12%",
            render: (_, record) => (
                <Tag color={record.resubscribedAt ? "green" : "red"}>
                    {record.resubscribedAt ? "Resubscribed" : "Unsubscribed"}
                </Tag>
            ),
            filters: [
                { text: "Unsubscribed", value: "unsubscribed" },
                { text: "Resubscribed", value: "resubscribed" },
            ],
            onFilter: (value, record) => {
                if (value === "unsubscribed") return !record.resubscribedAt
                if (value === "resubscribed") return !!record.resubscribedAt
                return true
            },
        },
        {
            title: "Unsubscribed At",
            dataIndex: "unsubscribedAt",
            key: "unsubscribedAt",
            width: "18%",
            render: (date: string) => (
                <Text>{new Date(date).toLocaleString()}</Text>
            ),
            sorter: (a, b) => new Date(a.unsubscribedAt).getTime() - new Date(b.unsubscribedAt).getTime(),
            defaultSortOrder: "descend",
        },
        {
            title: "Resubscribed At",
            dataIndex: "resubscribedAt",
            key: "resubscribedAt",
            width: "18%",
            render: (date: string | null) => (
                <Text>{date ? new Date(date).toLocaleString() : "-"}</Text>
            ),
            sorter: (a, b) => {
                if (!a.resubscribedAt && !b.resubscribedAt) return 0
                if (!a.resubscribedAt) return 1
                if (!b.resubscribedAt) return -1
                return new Date(a.resubscribedAt).getTime() - new Date(b.resubscribedAt).getTime()
            },
        },
        {
            title: "IP Address",
            dataIndex: "ipAddress",
            key: "ipAddress",
            width: "12%",
            render: (ip: string) => (
                <Text style={{ fontFamily: "monospace", fontSize: "12px" }}>
                    {ip}
                </Text>
            ),
        },
        {
            title: "User Agent",
            dataIndex: "userAgent",
            key: "userAgent",
            width: "15%",
            render: (userAgent: string) => (
                <Text 
                    ellipsis={{ tooltip: userAgent }}
                    style={{ fontSize: "12px", maxWidth: "200px" }}
                >
                    {userAgent}
                </Text>
            ),
        },
    ]

    const { unsubscribes = [], pagination } = data.data || {}

    return (
        <ContainerFull style={{ fontFamily: '"Rubik", sans-serif' }}>
            <Space direction="vertical" size="large" style={{ width: "100%" }}>
                <div>
                    <Title level={3}>Unsubscribed Emails </Title>
                    
                </div>

                <Card>
                    <Space direction="vertical" size="middle" style={{ width: "100%" }}>
                        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                            <Search
                                placeholder="Search by email address..."
                                allowClear
                                enterButton="Search"
                                size="large"
                                style={{ width: 400 }}
                                onSearch={handleSearch}
                                onChange={(e) => {
                                    if (!e.target.value) {
                                        handleSearch("")
                                    }
                                }}
                            />
                            <Space>
                                <Text strong>
                                    Total: {pagination?.total || 0} emails
                                </Text>
                                <Text type="secondary">
                                    Active Unsubscribes: {
                                        unsubscribes.filter(record => !record.resubscribedAt).length
                                    }
                                </Text>
                            </Space>
                        </div>

                        <Table
                            columns={columns}
                            dataSource={unsubscribes.map((record, index) => ({
                                ...record,
                                key: `${record.email}-${index}`,
                            }))}
                            pagination={{
                                current: pagination?.page || 1,
                                pageSize: pagination?.pageSize || 50,
                                total: pagination?.total || 0,
                                showSizeChanger: true,
                                showQuickJumper: true,
                                showTotal: (total, range) =>
                                    `${range[0]}-${range[1]} of ${total} emails`,
                                pageSizeOptions: ["25", "50", "100", "200"],
                            }}
                            onChange={handleTableChange}
                            size="middle"
                            scroll={{ x: 1000 }}
                            loading={isPending}
                        />
                    </Space>
                </Card>

                <Card title="Unsubscribe URL Generator">
                    <UnsubscribeUrlGenerator />
                </Card>
            </Space>
        </ContainerFull>
    )
}
